import { SetStateAction } from "react";
import { makeCygnusApiCall } from "../../../api/api";
import { FormRecord } from "../ClientCreator/ClientCreator.utils";

/**
 * Fetches details of a specific form by its ID and updates the form state.
 *
 * @param {number} formId - The ID of the form to fetch.
 * @param {Function} setFormDetails - Setter function to update form data state.
 */
export const fetchFormData = async (
  formId: number,
  setFormDetails: {
    (value: SetStateAction<FormRecord>): void;
    (arg: any): void;
  }
) => {
  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/form/${formId}/`,
    show_message: false,
    customized_success_message: "Fetched form data.",
    customized_failure_message:
      "Unable to fetch your form, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occured while fetching your client, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    setFormDetails(response.data[0]);
  }
};

/**
 * Fetches background check document types and updates the file input options.
 *
 * @param {Function} setFileInputs - Setter function to store document types.
 */
export const fetchDocumentTypes = async (setFileInputs: (arg: any) => void) => {
  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/firm_reference_data?ReferenceDataDomain=BackgroundCheckDocumentType`,
    show_message: true,
    customized_success_message: "Fetched documents data.",
    customized_failure_message:
      "Unable to fetch your form, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occured while fetching your client, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    setFileInputs(response.data);
  }
};

/**
 * Sends a request to approve or update the approval status of a client record.
 * Optionally uploads an approval PDF document along with the request.
 *
 * @param message - The approval message provided by the approver.
 * @param sharedData - The shared data containing client and form identifiers and approval status.
 * @param setSharedData - Function to update shared data state.
 * @param onHide - Callback to hide the modal or close the UI on successful approval.
 * @param refresh - Callback to refresh the parent data.
 * @param pdfBlob - Optional PDF blob to upload with the approval.
 */
export const approveRecord = async (
  message: string,
  sharedData: any,
  setSharedData: (data: any) => void,
  onHide: () => void,
  refresh: () => void,
) => {
  setSharedData((prev: any) => ({ ...prev, approvalAPIProgress: true }));

  try {
    const payload = {
      FormId: sharedData.onboardingFormId || sharedData.attestFormId || sharedData.taxAcceptanceFormId || sharedData.auditAcceptanceFormId,
      ClientMasterId: sharedData.clientMasterId,
      FirmDetailId: localStorage.getItem("userId") || 0,
      ApprovalStatus: sharedData.Status,
      ApprovalMessage: message,
    };

    const response = await makeCygnusApiCall({
      type: "PUT",
      payload: payload,
      url: "/form/approval/",
      show_message: true,
      customized_error_message:
      "Error occurred during approval process. Please contact support.",
      isblob: false,
      forMDM: true,
    });

    if (response.success) {
      onHide();
      refresh();
    }
  } catch (error) {
    console.error("Approval error:", error);
  } finally {
    setSharedData((prev: any) => ({ ...prev, approvalAPIProgress: false }));
  }
};
